import moment from "moment";
import { orderBy } from "lodash";
import { DateValueType } from "react-tailwindcss-datepicker";
import { API_URL } from "@redux/api";
import axios, { AxiosResponse } from "axios";
import { Link, useNavigate } from "react-router-dom";
import { useCallback, useEffect, useState } from "react"
import { FiChevronLeft, FiPlusCircle } from "react-icons/fi";

import { LoadingData, NotificationType } from "react-app-env";

import LoadingCard from "@components/loadingSchedule/LoadingCard";
import Loading from "@components/common/Loading";
import Notification from "@components/common/Notification";
import DatepickerInput from "@components/form/DatepickerInput";
import { TextInputDefault } from "@components/form/TextInput";
import dayjs from "dayjs";

type dateRangeType = {
  startDate: string;
  endDate: string;
};

type filtersType = {
  id: string;
  value: string | null | dateRangeType;
};

type fetchProps = { filters: filtersType[] | [] };

const JadwalLoadingInRoute = () => {
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [notification, setNotification] = useState<NotificationType | null>(null);

  const [selectedFilter, setSelectedFilter] = useState<"eventLocation" | "eventDate" | null>(null);

  const [loadingInData, setLoadingInData] = useState<LoadingData[] | []>([]);
  const [eventLocationFilter, setEventLocationFilter] = useState<string>("");
  const [dateFilter, setDateFilter] = useState<DateValueType | null>(null);

  const fetchLoadingData = useCallback(async ({ filters }: fetchProps) => {
    setIsLoading(true);
    setNotification(null);

    const payload = { filters };
    const response: AxiosResponse = await axios.post(`${API_URL}/loading/list`, payload);

    const { status, data } = response;

    if (status === 200) {
      const loadingDataList = data.data;

      const loadingInData = loadingDataList.filter((item: LoadingData) => item.type.code === "loading-in");
      const sortedLoadingData = orderBy(loadingInData, (item: LoadingData) => {
        return moment(item.loadingDate, 'DD/MM/YYYY');
      }, ['desc']);

      setLoadingInData(sortedLoadingData);
      setIsLoading(false);
    } else {
      setNotification({
        isError: true,
        title: "Error",
        text: data.message,
      });
      setIsLoading(false);
    };
  }, []);

  useEffect(() => {
    let eventDateFilter = null;

    if(dateFilter && dateFilter.endDate && dateFilter.startDate) {
      eventDateFilter = {
        startDate: dayjs(dateFilter.startDate).toISOString(),
        endDate: dayjs(dateFilter.endDate).toISOString(),
      }
    };

    fetchLoadingData({ 
      filters: [
        {id: "eventLocation", value: eventLocationFilter},
        {id: "eventDate", value: eventDateFilter},
      ]
    });
    return () => { };
  }, [fetchLoadingData, dateFilter, eventLocationFilter]);

  return (
    <div>
      <div className="bg-white rounded-xl p-4">
        <div className='flex items-center'>
          <button onClick={() => navigate("/jadwal-loading")} className='pr-4'>
            <FiChevronLeft size={25} />
          </button>
          <h1 className="text-center text-xl w-full md:text-2xl lg:text-4xl font-semibold">Jadwal Loading IN</h1>
        </div>
      </div>

      <div className="mt-5">
        <Notification notification={notification} />
      </div>

      <div className="mt-10">
        <Link
          className="w-full bg-sky-400 p-4 rounded-xl flex items-center justify-center gap-2 text-white"
          to={"/jadwal-loading/in/create"}
        >
          <FiPlusCircle />
          Buat Jadwal Baru
        </Link>
      </div>

      {/* FILTER */}
      <div className="bg-white p-4 rounded-xl mt-5">
        <div className="flex items-center justify-between">
          <h2 className="my-0 font-semibold">Filter Pencarian</h2>
          <button
            className="text-sm px-3 text-red-600"
            onClick={() => {
              setEventLocationFilter("");
              setDateFilter(null);
              setSelectedFilter(null);
            }}
          >
            Hapus filter
          </button>
        </div>
        <hr className="my-3" />
        <div className="flex items-center justify-around gap-3">
          <button
            className={`w-full py-2 rounded-xl ${selectedFilter === "eventLocation" ? "bg-orange-500 text-white" : "bg-gray-200"}`}
            onClick={() => {
              setDateFilter(null);
              setSelectedFilter("eventLocation")
            }}
          >
            Lokasi Acara
          </button>
          <button
            className={`w-full py-2 rounded-xl ${selectedFilter === "eventDate" ? "bg-orange-500 text-white" : "bg-gray-200"}`}
            onClick={() => {
              setEventLocationFilter("");
              setSelectedFilter("eventDate");
            }}
          >
            Tanggal Acara
          </button>
        </div>
        {selectedFilter === "eventDate" ?
          <>
            <hr className="my-3" />
            <div>
              <DatepickerInput
                showShortcuts={true}
                showFooter={true}
                configs={{
                  shortcuts: {
                    today: "Hari ini",
                    yesterday: "Kemarin",
                    past: period => period + " hari terakhir",
                    currentMonth: "Bulan ini",
                    pastMonth: "Bulan lalu"
                  },
                  footer: {
                    cancel: "Batal",
                    apply: "Terapkan"
                  }
                }}
                separator=" ~ "
                label="Berdasarkan Tanggal Acara"
                error={null}
                disabled={isLoading}
                name='eventDate'
                value={dateFilter}
                useRange={false}
                asSingle={false}
                maxDate={new Date()}
                className=''
                onChange={(value) => setDateFilter(value)}
              />
            </div>
          </>
          : selectedFilter === "eventLocation" ?
            <>
              <hr className="my-3" />
              <div>
                <TextInputDefault
                  name="eventLocationFilter"
                  label="Berdasarkan Lokasi Acara"
                  value={eventLocationFilter}
                  placeholder="Masukkan nama lokasi"
                  onChange={({ target }) => {
                    setEventLocationFilter(target.value);
                  }}
                  disabled={isLoading}
                  error={null}
                />
              </div>
            </>
            : null
        }
      </div>

      { isLoading ? 
        <div className='mt-5 flex items-center justify-center bg-white rounded-xl h-[250px]'>
          <Loading loading={isLoading} type='beat-loader' size={10} parentClass='text-sm' />
        </div>
        : loadingInData.length === 0 ?
          <div className="mt-5 bg-white h-[200px] flex flex-col items-center justify-center">
            <h3 className="font-semibold text-lg">Data tidak ditemukan</h3>
            <p className="mt-2">Silahkan buat jadwal loading in</p>
          </div>
        : <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {loadingInData.map((item, idx) => {
            return (
              <LoadingCard
                key={`loading-in_${idx}`}
                data={item}
                loadingType="in"
              />
            )
          })}
        </div>
      }
    </div>
  )
}

export default JadwalLoadingInRoute